"use client"

import Image from "next/image"
import { motion, AnimatePresence } from "framer-motion"
import { useQueryClient } from "@tanstack/react-query"

import iconStats1024Img from "@packages/base/assets/icon-stats-1024.png"
import useGetAuthentication from "@packages/base/hooks/use-get-authentication"
import NavLink from "@packages/web/components/NavLink"
import SignLink from "@packages/web/components/SignLink"

import Holygrail from "@/components/Holygrail"

const signinWithRedirect = "/auth?sign=signin&redirect=/dashboard"

function LandingCta({
  isAuthenticated,
  isAuthenticationSuccess,
  isEditor,
}: {
  isAuthenticated: boolean
  isAuthenticationSuccess: boolean
  isEditor: boolean
}) {
  if (!isAuthenticationSuccess) {
    return (
      <NavLink className="w-full max-w-[280px]" to={signinWithRedirect} variant="default">
        S&apos;authentifier
      </NavLink>
    )
  }

  if (isAuthenticated && isEditor) {
    return (
      <NavLink className="w-full max-w-[280px]" to="/dashboard" variant="default">
        Entrer
      </NavLink>
    )
  }

  if (isAuthenticated) {
    return (
      <div className="flex w-full max-w-[280px] flex-col items-center gap-4">
        <p className="text-center text-sm text-muted-foreground">
          Votre compte n&apos;a pas accès au backoffice. Contactez un administrateur.
        </p>
        <SignLink signoutTo={signinWithRedirect} />
      </div>
    )
  }

  return (
    <NavLink className="w-full max-w-[280px]" to={signinWithRedirect} variant="default">
      S&apos;authentifier
    </NavLink>
  )
}

export default function PageClient() {
  const queryClient = useQueryClient()
  const { data: authentication, isSuccess: isAuthenticationSuccess } = useGetAuthentication({
    redirectIfNotAuthenticated: false,
  }, queryClient)
  const { isAuthenticated, isEditor } = authentication || {}

  return (
    <Holygrail noFooter noBacklink noHeader>
      <div className="landing-blue-bg relative flex h-full min-h-0 flex-col items-center justify-center overflow-hidden px-6 py-16">
        <div className="relative z-10 flex max-w-lg flex-col items-center text-center">
          <Image
            src={iconStats1024Img}
            alt=""
            width={96}
            height={96}
            priority
            className="mb-4 h-20 w-20 object-contain sm:h-24 sm:w-24"
          />

          <p className="mb-2 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
            Ernest
          </p>
          <p className="mb-10 text-sm font-medium text-nav-muted sm:text-base">
            backoffice
          </p>

          <AnimatePresence mode="wait">
            <motion.div
              key={
                isAuthenticationSuccess && isAuthenticated && isEditor
                  ? "editor"
                  : isAuthenticationSuccess && isAuthenticated
                    ? "authenticated"
                    : "unauthenticated"
              }
              initial={false}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="flex w-full flex-col items-center"
            >
              <LandingCta
                isAuthenticated={!!isAuthenticated}
                isAuthenticationSuccess={isAuthenticationSuccess}
                isEditor={!!isEditor}
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Holygrail>
  )
}
